import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { apiFetch, AppContext } from '../bizsuite-app';

/**
 * Home — the Nouveau-mode tile launchpad.
 *
 * Renders only when the user has nav_mode = 'nouveau'. Layout hides the
 * Sidebar in that mode, so this page IS the navigation. Two main sections:
 *   1. Pinned — user-curated tiles (3-8 typical), each with optional live KPI.
 *   2. All apps — every available tile grouped by category. Pin/unpin from any.
 *
 * Tile counts come from /api/dashboard/tile-counts in a single round-trip so
 * the launchpad stays snappy. Pin order is persisted via /api/auth/me/preferences.
 */

/* ── Tile catalogue. Each tile defines:
 *    path:     route to navigate to (#/path)
 *    label:    display text on the tile
 *    category: section grouping in the "All apps" area
 *    countKey: optional — pulls a number from /api/dashboard/tile-counts
 *    subKey:   optional — pulls a sub-tile subtitle ("7 hot", etc.)
 *    color:    accent ramp from the BizSuite neon palette
 * ── */
const COLOR = {
  blue:   { fill: 'rgba(59,130,246,0.10)',  stroke: 'rgba(59,130,246,0.40)',  text: '#60A5FA' },
  pink:   { fill: 'rgba(244,114,182,0.10)', stroke: 'rgba(244,114,182,0.40)', text: '#F472B6' },
  green:  { fill: 'rgba(34,197,94,0.10)',   stroke: 'rgba(34,197,94,0.40)',   text: '#4ADE80' },
  amber:  { fill: 'rgba(245,158,11,0.10)',  stroke: 'rgba(245,158,11,0.40)',  text: '#FBBF24' },
  violet: { fill: 'rgba(167,139,250,0.10)', stroke: 'rgba(167,139,250,0.40)', text: '#A78BFA' },
  cyan:   { fill: 'rgba(0,224,255,0.10)',   stroke: 'rgba(0,224,255,0.40)',   text: '#6FE6FF' },
  rose:   { fill: 'rgba(251,113,133,0.10)', stroke: 'rgba(251,113,133,0.40)', text: '#FB7185' },
  teal:   { fill: 'rgba(20,184,166,0.10)',  stroke: 'rgba(20,184,166,0.40)',  text: '#5EEAD4' },
  coral:  { fill: 'rgba(255,127,80,0.10)',  stroke: 'rgba(255,127,80,0.40)',  text: '#FF9966' },
};

// Order matters — this is the visual order tiles appear in within each section.
const TILES = [
  // ── Sales ──
  { path: '/customers',         label: 'Customers',         category: 'Sales',     countKey: 'customers',     color: 'blue'   },
  { path: '/crm/leads',         label: 'Leads',             category: 'Sales',     countKey: 'leads',         subKey: 'hotLeads', subSuffix: 'hot', color: 'pink' },
  { path: '/crm/opportunities', label: 'Opportunities',     category: 'Sales',     countKey: 'opportunities', subKey: 'openOpps', subSuffix: 'open', color: 'amber' },
  { path: '/crm/pipeline',      label: 'Pipeline',          category: 'Sales',     color: 'amber' },
  { path: '/contracts',         label: 'Contracts',         category: 'Sales',     countKey: 'contracts',     color: 'violet' },
  { path: '/estimates',         label: 'Estimates',         category: 'Sales',     color: 'violet' },
  { path: '/proposals',         label: 'Proposals',         category: 'Sales',     color: 'violet' },

  // ── Delivery ──
  { path: '/projects',          label: 'Projects',          category: 'Delivery',  countKey: 'projects',      color: 'cyan'   },
  { path: '/resources',         label: 'Allocation',        category: 'Delivery',  color: 'teal'   },
  { path: '/timesheets',        label: 'Timesheets',        category: 'Delivery',  countKey: 'timesheets',    subSuffix: 'pending', color: 'cyan' },

  // ── HCM ──
  { path: '/employees',         label: 'Employees',         category: 'HCM',       countKey: 'employees',     color: 'pink'   },
  { path: '/employees/live',    label: 'Workforce live',    category: 'HCM',       color: 'pink'   },
  { path: '/org-structure',     label: 'Org structure',     category: 'HCM',       color: 'pink'   },
  { path: '/recruitment',       label: 'Recruitment',       category: 'HCM',       color: 'pink'   },
  { path: '/group-tags',        label: 'Group tags',        category: 'HCM',       color: 'pink'   },
  { path: '/grades',            label: 'Grades',            category: 'HCM',       color: 'pink'   },
  { path: '/salary-schemas',    label: 'Salary schemas',    category: 'HCM',       color: 'pink'   },

  // ── Finance ──
  { path: '/billing',           label: 'Billing & invoices',category: 'Finance',   countKey: 'invoices',      color: 'amber'  },
  { path: '/revenue',           label: 'Receivables',       category: 'Finance',   color: 'amber'  },
  { path: '/finance',           label: 'Finance',           category: 'Finance',   color: 'green'  },
  { path: '/accounting-guide',  label: 'Accounting guide',  category: 'Finance',   color: 'green'  },
  { path: '/salary',            label: 'Salary & payroll',  category: 'Finance',   color: 'amber'  },
  { path: '/reimbursements',    label: 'Reimbursements',    category: 'Finance',   color: 'amber'  },

  // ── Procurement ──
  { path: '/vendors',           label: 'Vendors',           category: 'Procurement', color: 'coral' },
  { path: '/procurement',       label: 'Procurement',       category: 'Procurement', color: 'coral' },

  // ── Self Service ──
  { path: '/ess/leave',         label: 'My leave / WFH',    category: 'Self Service', color: 'cyan' },
  { path: '/reminders',         label: 'Reminders',         category: 'Self Service', countKey: 'reminders', subSuffix: 'open', color: 'rose' },

  // ── Admin ──
  { path: '/admin',             label: 'Administration',    category: 'Admin',     color: 'rose'   },
  { path: '/offices',           label: 'Offices',           category: 'Admin',     color: 'rose'   },
  { path: '/metadata',          label: 'Metadata',          category: 'Admin',     color: 'rose'   },
  { path: '/admin/integrations',label: 'Integrations',      category: 'Admin',     color: 'rose'   },
  { path: '/workflow',          label: 'Workflows',         category: 'Admin',     color: 'cyan'   },
];

const CATEGORIES = ['Sales', 'Delivery', 'HCM', 'Finance', 'Procurement', 'Self Service', 'Admin'];

export default function Home() {
  const ctx = useContext(AppContext);
  const currentUser = ctx?.currentUser || null;
  const navigate = ctx?.navigate || ((p) => { window.location.hash = '#' + p; });

  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [pinned, setPinned] = useState([]);

  // Hydrate pins from currentUser on mount and whenever the user changes.
  // Falls back to a sensible default of the four most-used tiles for first-
  // time Nouveau users, so the launchpad isn't empty.
  useEffect(() => {
    const fromUser = Array.isArray(currentUser?.pinnedTiles) ? currentUser.pinnedTiles : null;
    if (fromUser && fromUser.length) setPinned(fromUser);
    else setPinned(['/customers', '/crm/opportunities', '/projects', '/employees']);
  }, [currentUser?.id]);

  // Single-shot tile counts. /api/dashboard/tile-counts wraps each query in
  // safeQuery so a missing table on a stale tenant returns 0 rather than 500.
  useEffect(() => {
    apiFetch('/api/dashboards/tile-counts')
      .then(r => r.ok ? r.json() : {})
      .then(d => setCounts(d || {}))
      .catch(() => setCounts({}));
  }, []);

  // Persist a pin/unpin to /api/auth/me/preferences. Optimistic update so the
  // tile reflects the new state immediately; backend write is fire-and-forget.
  const togglePin = useCallback((path) => {
    setPinned(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      apiFetch('/api/auth/me/preferences', {
        method: 'PUT',
        body: JSON.stringify({ pinnedTiles: next }),
      }).catch(() => {});
      return next;
    });
  }, []);

  // Pinned tiles, in pin order. Filter out any pins that no longer match a
  // known tile (catalogue may shrink between deploys).
  const pinnedTiles = useMemo(() => {
    const byPath = Object.fromEntries(TILES.map(t => [t.path, t]));
    return pinned.map(p => byPath[p]).filter(Boolean);
  }, [pinned]);

  // Filtered "All apps" — search hits label + category, filter chip narrows by category.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return TILES.filter(t => {
      if (filter !== 'All' && t.category !== filter) return false;
      if (!q) return true;
      return t.label.toLowerCase().includes(q) || t.category.toLowerCase().includes(q);
    });
  }, [filter, search]);

  const grouped = useMemo(() => {
    const g = {};
    for (const cat of CATEGORIES) g[cat] = [];
    for (const t of filtered) (g[t.category] = g[t.category] || []).push(t);
    return g;
  }, [filtered]);

  function formatCount(t) {
    if (!t.countKey) return null;
    const v = counts[t.countKey];
    if (v == null) return null;
    if (typeof v === 'number' && t.countKey === 'receivables') {
      // Receivables comes back as a numeric value, not a count — render in
      // Indian-grouped notation matching the rest of the app.
      return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);
    }
    return new Intl.NumberFormat('en-IN').format(v);
  }
  function formatSub(t) {
    if (!t.subKey || !counts.sub) return null;
    const v = counts.sub[t.subKey];
    if (v == null) return null;
    return `${v} ${t.subSuffix || ''}`.trim();
  }

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();
  const fname = currentUser?.firstName || (currentUser?.fullName || '').split(' ')[0] || '';

  return (
    <div style={{ padding: '8px 4px 40px' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 600, color: '#E5E7EB' }}>
        {greeting}{fname ? `, ${fname}` : ''}
      </h1>
      <div style={{ color: '#94A3B8', fontSize: 13, marginBottom: 18 }}>
        Pin the apps you use most. The Classic sidebar is one click away in the header.
      </div>

      {/* Search + filter chips */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search apps…"
          style={{
            flex: '1 1 240px', maxWidth: 360, padding: '8px 12px',
            background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 13,
            border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['All', ...CATEGORIES].map(c => {
            const on = filter === c;
            return (
              <button key={c} onClick={() => setFilter(c)} style={{
                padding: '5px 12px', fontSize: 12, fontWeight: 500, borderRadius: 999,
                border: '1px solid ' + (on ? 'transparent' : 'rgba(255,255,255,0.10)'),
                background: on ? 'rgba(0,224,255,0.18)' : 'rgba(255,255,255,0.04)',
                color: on ? '#6FE6FF' : '#94A3B8', cursor: 'pointer',
              }}>{c}</button>
            );
          })}
        </div>
      </div>

      {/* Pinned section */}
      {pinnedTiles.length > 0 && (
        <>
          <SectionHeader>Pinned</SectionHeader>
          <TileGrid>
            {pinnedTiles.map(t => (
              <Tile key={t.path} tile={t} pinned
                onClick={() => navigate(t.path)}
                onPin={() => togglePin(t.path)}
                count={formatCount(t)} sub={formatSub(t)} />
            ))}
          </TileGrid>
        </>
      )}

      {/* All apps grouped by category */}
      {CATEGORIES.map(cat => grouped[cat] && grouped[cat].length > 0 && (
        <React.Fragment key={cat}>
          <SectionHeader>{cat}</SectionHeader>
          <TileGrid>
            {grouped[cat].map(t => (
              <Tile key={t.path} tile={t} pinned={pinned.includes(t.path)}
                onClick={() => navigate(t.path)}
                onPin={() => togglePin(t.path)}
                count={formatCount(t)} sub={formatSub(t)} />
            ))}
          </TileGrid>
        </React.Fragment>
      ))}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div style={{
      fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.8px',
      fontWeight: 600, margin: '18px 2px 10px',
    }}>{children}</div>
  );
}

function TileGrid({ children }) {
  return (
    <div style={{
      display: 'grid', gap: 10,
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    }}>{children}</div>
  );
}

function Tile({ tile, pinned, count, sub, onClick, onPin }) {
  const c = COLOR[tile.color] || COLOR.cyan;
  const hasKpi = count != null;
  return (
    <div style={{
      position: 'relative', cursor: 'pointer',
      background: c.fill, border: `1px solid ${c.stroke}`, borderRadius: 12,
      padding: '14px 14px 12px', minHeight: hasKpi ? 110 : 78,
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      transition: 'transform .1s, box-shadow .15s',
    }}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 18px ${c.stroke}`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Pin toggle — top right. Stops click propagation so it doesn't navigate. */}
      <button
        onClick={e => { e.stopPropagation(); onPin && onPin(); }}
        title={pinned ? 'Unpin from launchpad' : 'Pin to launchpad'}
        style={{
          position: 'absolute', top: 8, right: 8, width: 22, height: 22,
          border: 'none', background: 'transparent', cursor: 'pointer',
          color: pinned ? c.text : 'rgba(148,163,184,0.50)', fontSize: 14, padding: 0,
        }}>{pinned ? '★' : '☆'}</button>
      <div style={{ fontSize: 13, fontWeight: 500, color: '#E5E7EB', paddingRight: 22 }}>
        {tile.label}
      </div>
      {hasKpi && (
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, color: c.text, lineHeight: 1 }}>{count}</div>
          {sub && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>{sub}</div>}
        </div>
      )}
    </div>
  );
}
